import { RootState } from "@/redux/store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { extractHolidayFilterData } from "../lib/extract-holiday-filter-data";
import {
  buildHolidayFilterState,
  clampHolidayPriceRange,
} from "../lib/filter-holiday-result";
import {
  IHolidayFilterOptionData,
  IHolidayFilterState,
  IHolidaySearchQueryParams,
  IHolidaySearchResult,
} from "../types/holiday-search-type";

interface IHolidayFilterSliceState {
  hasValue: boolean;
  defaultOption: IHolidayFilterOptionData;
  selectedOption: IHolidayFilterState;
}

type ISetHolidayFilterSourcePayload = {
  results: IHolidaySearchResult[];
  searchParams: IHolidaySearchQueryParams;
  keepFilters?: boolean;
};

function createEmptyDefaultOption(): IHolidayFilterOptionData {
  return {
    minPrice: 0,
    maxPrice: 0,
    holidayTypes: [],
    holidayFor: [],
    durations: [],
  };
}

function createDefaultSelectedOption(
  defaultOption: IHolidayFilterOptionData,
): IHolidayFilterState {
  return {
    query: "",
    minPrice: defaultOption.minPrice,
    maxPrice: defaultOption.maxPrice,
    holidayTypes: [],
    holidayFor: [],
    durations: [],
  };
}

function hasActiveHolidayFilter(
  selectedOption: IHolidayFilterState,
  defaultOption: IHolidayFilterOptionData,
) {
  if (selectedOption.query.trim()) {
    return true;
  }

  if (selectedOption.holidayTypes.length > 0) {
    return true;
  }

  if (selectedOption.durations.length > 0) {
    return true;
  }

  const min = selectedOption.minPrice ?? defaultOption.minPrice;
  const max = selectedOption.maxPrice ?? defaultOption.maxPrice;

  return min !== defaultOption.minPrice || max !== defaultOption.maxPrice;
}

const initialDefaultOption = createEmptyDefaultOption();
const initialState: IHolidayFilterSliceState = {
  hasValue: false,
  defaultOption: initialDefaultOption,
  selectedOption: createDefaultSelectedOption(initialDefaultOption),
};

const extractHolidayFilter = createSlice({
  name: "holiday_filter",
  initialState,
  reducers: {
    setHolidayForExtractFilter: (
      state,
      action: PayloadAction<ISetHolidayFilterSourcePayload>,
    ) => {
      const { results, searchParams, keepFilters = false } = action.payload;
      const defaultOption = extractHolidayFilterData(results);
      state.defaultOption = defaultOption;

      const fromSearch = clampHolidayPriceRange(
        buildHolidayFilterState(searchParams, defaultOption),
        defaultOption,
      );

      if (!keepFilters || !state.hasValue) {
        state.selectedOption = fromSearch;
      } else {
        state.selectedOption = clampHolidayPriceRange(
          state.selectedOption,
          defaultOption,
        );
      }

      state.hasValue = hasActiveHolidayFilter(
        state.selectedOption,
        state.defaultOption,
      );
    },
    setHolidayFilters: (
      state,
      action: PayloadAction<Partial<IHolidayFilterState>>,
    ) => {
      state.selectedOption = clampHolidayPriceRange(
        {
          ...state.selectedOption,
          ...action.payload,
        },
        state.defaultOption,
      );

      state.hasValue = hasActiveHolidayFilter(
        state.selectedOption,
        state.defaultOption,
      );
    },
    clearAllHolidayFilters: (state) => {
      state.selectedOption = createDefaultSelectedOption(state.defaultOption);
      state.hasValue = false;
    },
    removeHolidayFilterItem: (
      state,
      action: PayloadAction<{
        key: keyof IHolidayFilterState;
        value?: string;
      }>,
    ) => {
      const { key, value } = action.payload;
      const current = state.selectedOption[key];

      if (Array.isArray(current)) {
        state.selectedOption[key] = (current.filter(
          (item) => item !== value,
        ) as unknown) as never;
      } else if (key === "query") {
        state.selectedOption.query = "";
      } else if (key === "minPrice") {
        state.selectedOption.minPrice = state.defaultOption.minPrice;
      } else if (key === "maxPrice") {
        state.selectedOption.maxPrice = state.defaultOption.maxPrice;
      }

      state.selectedOption = clampHolidayPriceRange(
        state.selectedOption,
        state.defaultOption,
      );
      state.hasValue = hasActiveHolidayFilter(
        state.selectedOption,
        state.defaultOption,
      );
    },
  },
});

export const {
  setHolidayForExtractFilter,
  setHolidayFilters,
  clearAllHolidayFilters,
  removeHolidayFilterItem,
} = extractHolidayFilter.actions;

export const useHolidayFilter = (state: RootState) => state.extractHolidayFilter;

export default extractHolidayFilter;
